import type { Node } from "@/lib/tool-kit/calculus/ast";
import { differentiate } from "@/lib/tool-kit/calculus/differentiate";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { type ExprError, parseExpression } from "@/lib/tool-kit/calculus/parse";
import {
  type ExtremumKind,
  findExtrema,
  findIntersections,
  findZeros,
} from "@/lib/tool-kit/calculus/plot";

/**
 * What the graphing calculator knows about each line of input and about the picture:
 * the parsed functions, and the zeros, intersections and turning points inside the window.
 */

export interface GraphFunction {
  /** Position of the input row (0-based), used for its colour and label. */
  row: number;
  ast: Node;
  f: (x: number) => number;
  /** The derivative, for locating turning points. */
  df: (x: number) => number;
  decimals: boolean;
}

export type RowResult =
  | { kind: "blank" }
  | { kind: "error"; error: ExprError }
  | { kind: "many-variables" }
  | { kind: "ok"; fn: GraphFunction };

/** "y = x^2", "f(x) = x^2" and "x^2" all mean the same thing. */
const LEADING_EQUALS = /^\s*(?:y|f\s*\(\s*x\s*\))\s*=\s*/i;

/** Parse one input row. Only the variable x is allowed. */
export function parseRow(text: string, row: number): RowResult {
  const body = text.replace(LEADING_EQUALS, "");
  if (body.trim() === "") {
    return { kind: "blank" };
  }
  const parsed = parseExpression(body);
  if (!parsed.ok) {
    return { kind: "error", error: parsed.error };
  }
  if (parsed.variables.some((name) => name !== "x")) {
    return { kind: "many-variables" };
  }
  return {
    kind: "ok",
    fn: {
      row,
      ast: parsed.ast,
      f: compile(parsed.ast, "x"),
      df: compile(differentiate(parsed.ast, "x").derivative, "x"),
      decimals: parsed.usesDecimals,
    },
  };
}

export interface Zero {
  row: number;
  x: number;
}

export interface Crossing {
  rows: [number, number];
  x: number;
  y: number;
}

export interface TurningPoint {
  row: number;
  x: number;
  y: number;
  kind: ExtremumKind;
}

export interface Features {
  zeros: Zero[];
  crossings: Crossing[];
  turning: TurningPoint[];
}

function perFunction(
  functions: GraphFunction[],
  xMin: number,
  xMax: number,
  want: { zeros: boolean; turning: boolean }
): { zeros: Zero[]; turning: TurningPoint[] } {
  const zeros: Zero[] = [];
  const turning: TurningPoint[] = [];
  for (const fn of functions) {
    if (want.zeros) {
      zeros.push(
        ...findZeros(fn.f, xMin, xMax).map((x) => ({ row: fn.row, x }))
      );
    }
    if (want.turning) {
      turning.push(
        ...findExtrema(fn.f, fn.df, xMin, xMax).map((point) => ({
          row: fn.row,
          ...point,
        }))
      );
    }
  }
  return { zeros, turning };
}

function pairwise(
  functions: GraphFunction[],
  xMin: number,
  xMax: number
): Crossing[] {
  const crossings: Crossing[] = [];
  functions.forEach((a, i) => {
    for (const b of functions.slice(i + 1)) {
      for (const hit of findIntersections(a.f, b.f, xMin, xMax)) {
        crossings.push({ rows: [a.row, b.row], x: hit.x, y: hit.y });
      }
    }
  });
  return crossings;
}

/** Zeros, pairwise intersections and local extrema of every function over [xMin, xMax]. */
export function findFeatures(
  functions: GraphFunction[],
  xMin: number,
  xMax: number,
  want: { zeros: boolean; crossings: boolean; turning: boolean }
): Features {
  const { zeros, turning } = perFunction(functions, xMin, xMax, want);
  const crossings = want.crossings ? pairwise(functions, xMin, xMax) : [];
  return { zeros, crossings, turning };
}

/** Sample f at count values from `start` with the given step. */
export function valueTable(
  functions: GraphFunction[],
  start: number,
  step: number,
  count: number
): { x: number; values: number[] }[] {
  return Array.from({ length: count }, (_, i) => {
    // Multiply rather than accumulate, so 0.1 × 3 is not 0.30000000000000004 plus drift.
    const x = Number((start + i * step).toPrecision(12));
    return { x, values: functions.map((fn) => fn.f(x)) };
  });
}
